import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { Network } from './network.js'
import emojiData from 'unicode-emoji-json/data-by-emoji.json'
import * as topojson from 'topojson-client'
import worldData from 'world-atlas/countries-110m.json'

const GROUP_ORDER = [
    'Smileys & Emotion',
    'People & Body',
    'Animals & Nature',
    'Food & Drink',
    'Travel & Places',
    'Activities',
    'Objects',
    'Symbols',
    'Flags',
]

const GROUP_COLOR = {
    'Smileys & Emotion': '#f5c542',
    'People & Body':     '#f57842',
    'Animals & Nature':  '#5abf6e',
    'Food & Drink':      '#e05c8a',
    'Travel & Places':   '#4aa8e0',
    'Activities':        '#a066d3',
    'Objects':           '#70b8c8',
    'Symbols':           '#8899bb',
    'Flags':             '#cc5555',
}

const GRID_COLS = 40
const GRID_ROWS = 20
const GAP = 0.06

const EMOJIS = Object.keys(emojiData)
const EMOJI_INDEX = new Map(EMOJIS.map((e, i) => [e, i + 1]))

// Flags need the Noto webfont to render as flags on Windows; everything else
// uses the platform emoji font, so no other font slice is ever requested.
const FLAG_FONT = '"Noto Color Emoji", serif'
const isFlag = (emoji) => emojiData[emoji]?.group === 'Flags'
const emojiFont = (px, emoji) => `${px}px ${isFlag(emoji) ? FLAG_FONT : 'serif'}`

function applyFlagFont(el, emoji) {
    if (isFlag(emoji)) el.classList.add('emoji-flag')
}

// The emoji list runs ahead of the fonts installed on any given machine — Emoji
// 16.0/17.0 characters draw as a tofu box, and unsupported ZWJ sequences draw as
// their components side by side. Both are found by drawing the emoji once and
// comparing it against a known-missing glyph and against a known-good width.
// Flags are exempt: they come from the Noto webfont, which may still be loading.
function findRenderable(emojis) {
    const SIZE = 32
    const c = document.createElement('canvas')
    c.width = c.height = SIZE
    const ctx = c.getContext('2d', { willReadFrequently: true })
    ctx.textBaseline = 'top'
    ctx.font = `${SIZE * 0.75}px serif`

    // Hash the alpha channel only: shape is what differs, colour doesn't matter.
    function signature(text) {
        ctx.clearRect(0, 0, SIZE, SIZE)
        ctx.fillText(text, 0, 0)
        const { data } = ctx.getImageData(0, 0, SIZE, SIZE)
        let h = 0
        for (let i = 3; i < data.length; i += 4) h = (h * 31 + data[i]) | 0
        return h
    }

    const blank = signature('')
    const tofu  = signature('\uFFFF')          // permanently unassigned
    const maxWidth = ctx.measureText('\u{1F600}').width * 1.5

    const renderable = []
    const dropped = []
    for (const emoji of emojis) {
        if (isFlag(emoji)) { renderable.push(emoji); continue }
        const sig = signature(emoji)
        const ok = sig !== tofu && sig !== blank && ctx.measureText(emoji).width <= maxWidth
        ;(ok ? renderable : dropped).push(emoji)
    }

    if (dropped.length) {
        console.log(`[emojiworld] ${dropped.length} emoji have no glyph on this device:`,
                    dropped.map(e => `${e} ${emojiData[e]?.name} (v${emojiData[e]?.emoji_version})`).join(', '))
    }
    return renderable
}

// Numbers stay tied to the full catalogue, so they don't shift between devices.
const SHOWN = findRenderable(EMOJIS)

// --- Border carousel (decoration only, no interaction) ---
// Each edge keeps only enough items to cover itself; as the strip scrolls, items
// that leave one end are re-used at the other with the next emoji's content. A
// full strip would be ~1900 nodes per edge inside an 86,000px composited layer,
// which Chrome has to re-raster in chunks as it moves.
const SPEED = 190          // px/s
const carouselTracks = []
let   carouselPaused = false

const mod = (n, m) => ((n % m) + m) % m

function makeItem() {
    const item = document.createElement('span')
    item.className = 'carousel-item'

    const wrapper = document.createElement('span')
    wrapper.className = 'carousel-emoji-wrap'

    const emojiEl = document.createElement('span')
    emojiEl.className = 'carousel-emoji'

    const numEl = document.createElement('span')
    numEl.className = 'carousel-num'

    wrapper.append(emojiEl, numEl)
    item.appendChild(wrapper)
    return item
}

function setItem(item, emoji) {
    const group = emojiData[emoji]?.group ?? ''
    item.style.setProperty('--group-color', GROUP_COLOR[group] ?? '#8899bb')

    const emojiEl = item.firstElementChild.firstElementChild
    emojiEl.textContent = emoji
    emojiEl.classList.toggle('emoji-flag', isFlag(emoji))

    item.querySelector('.carousel-num').textContent = EMOJI_INDEX.get(emoji)
}

function trackDim(track) {
    return track.isHoriz ? window.innerWidth : window.innerHeight
}

// Keep enough items to cover the edge plus one leaving and one entering.
function ensureCount(track) {
    const want = Math.ceil(trackDim(track) / track.itemSize) + 2
    while (track.items.length < want) {
        const item = makeItem()
        setItem(item, SHOWN[mod(track.idx + track.items.length, SHOWN.length)])
        track.inner.appendChild(item)
        track.items.push(item)
    }
}

// Invariant: items[i] shows EMOJIS[(idx + i) % len].
function shiftTo(track, idx) {
    const len = SHOWN.length
    const delta = idx - track.idx

    if (Math.abs(delta) >= track.items.length) {
        track.idx = idx
        track.items.forEach((item, i) => setItem(item, SHOWN[mod(idx + i, len)]))
        return
    }

    for (let n = 0; n < delta; n++) {
        const item = track.items.shift()
        setItem(item, SHOWN[mod(track.idx + track.items.length + 1, len)])
        track.inner.appendChild(item)
        track.items.push(item)
        track.idx++
    }
    for (let n = 0; n > delta; n--) {
        const item = track.items.pop()
        setItem(item, SHOWN[mod(track.idx - 1, len)])
        track.inner.prepend(item)
        track.items.unshift(item)
        track.idx--
    }
}

function advanceTrack(track, dpx) {
    track.pos += dpx
    // invert tracks run the strip backwards, which also reverses on-screen travel.
    const signed = track.invert ? -track.pos : track.pos
    const idx = Math.floor(signed / track.itemSize)
    if (idx !== track.idx) shiftTo(track, idx)

    // Rounded to whole pixels so Chrome doesn't re-raster glyphs every frame.
    const t = Math.round(signed - idx * track.itemSize)
    track.inner.style.transform = track.isHoriz
        ? `translateX(${-t}px)`
        : `translateY(${-t}px)`
}

function buildTrack(innerId, dir) {
    const inner   = document.getElementById(innerId)
    const isHoriz = dir === 'left' || dir === 'right'
    // Stagger start indices so neighbouring edges don't show the same run of emojis.
    const track   = { inner, isHoriz, invert: dir === 'right' || dir === 'down',
                      items: [], itemSize: 0, pos: 0,
                      idx: carouselTracks.length * 480 }
    carouselTracks.push(track)

    // One probe item gives the real item size before the rest are created.
    const probe = makeItem()
    setItem(probe, SHOWN[mod(track.idx, SHOWN.length)])
    inner.appendChild(probe)
    track.items.push(probe)
    track.itemSize = isHoriz ? probe.offsetWidth : probe.offsetHeight
    // pos and idx describe the same point on the strip, so the stagger has to be
    // baked into both or the first frame snaps the track back to emoji #1.
    track.pos = (track.invert ? -1 : 1) * track.idx * track.itemSize
    ensureCount(track)
}

buildTrack('inner-top',    'right')
buildTrack('inner-right',  'down')
buildTrack('inner-bottom', 'left')
buildTrack('inner-left',   'up')

window.addEventListener('resize', () => {
    for (const track of carouselTracks) ensureCount(track)
})

// Driven from the single render loop at the bottom of this file rather than from
// its own requestAnimationFrame, so the two never contend for the same frame.
let lastCarouselTime = null
function advanceCarousel(ts) {
    if (lastCarouselTime !== null && !carouselPaused) {
        const dt = Math.min((ts - lastCarouselTime) / 1000, 0.05)
        for (const track of carouselTracks) advanceTrack(track, SPEED * dt)
    }
    lastCarouselTime = ts
}

// --- Hover tooltip (carousel items + picker buttons) ---
const TIP_TARGETS = '.carousel-item, .emoji-btn'

const tooltip  = document.getElementById('emoji-tooltip')
const tipEmoji = document.getElementById('tooltip-emoji')
const tipName  = document.getElementById('tooltip-name')
const tipGroup = document.getElementById('tooltip-group')
const tipNum   = document.getElementById('tooltip-num')

function hideTooltip() {
    tooltip.style.display = 'none'
    carouselPaused = false
}

document.addEventListener('mouseover', (e) => {
    const target = e.target.closest(TIP_TARGETS)
    if (!target) return
    const emoji = target.dataset.emoji ?? target.querySelector('.carousel-emoji')?.textContent
    if (!emoji) return

    // All edges hold together while a carousel emoji is hovered — pausing one
    // would leave it permanently out of phase with the rest of the ring.
    carouselPaused = target.classList.contains('carousel-item')

    const group = emojiData[emoji]?.group ?? ''
    const color = GROUP_COLOR[group] ?? '#8899bb'
    tipEmoji.textContent = emoji
    tipEmoji.classList.toggle('emoji-flag', isFlag(emoji))
    tipName.textContent  = emojiData[emoji]?.name ?? ''
    tipGroup.textContent = group
    tipGroup.style.color = color
    tipNum.textContent   = '#' + EMOJI_INDEX.get(emoji)
    tooltip.style.borderColor = color + '88'
    tooltip.style.display = 'flex'
})

document.addEventListener('mouseout', (e) => {
    if (!e.relatedTarget?.closest?.(TIP_TARGETS)) hideTooltip()
})

document.addEventListener('mousemove', (e) => {
    if (tooltip.style.display === 'none') return
    const tw = tooltip.offsetWidth
    const th = tooltip.offsetHeight
    let x = e.clientX + 16
    let y = e.clientY + 16
    if (x + tw > window.innerWidth  - 8) x = e.clientX - tw - 16
    if (y + th > window.innerHeight - 8) y = e.clientY - th - 16
    tooltip.style.left = x + 'px'
    tooltip.style.top  = y + 'px'
})

// --- World state ---
const tileState = {}
let myLocation = null

// --- Renderer ---
const canvas = document.querySelector('canvas.webgl')
const scene = new THREE.Scene()
scene.background = new THREE.Color('#1a1a2e')

const sizes = { width: window.innerWidth, height: window.innerHeight }

const cx = (GRID_COLS - 1) / 2
const cz = (GRID_ROWS - 1) / 2
const camera = new THREE.PerspectiveCamera(65, sizes.width / sizes.height, 0.1, 100)
camera.position.set(cx, 22, GRID_ROWS + 2)
scene.add(camera)

const renderer = new THREE.WebGLRenderer({ canvas, antialias: true })
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

const controls = new OrbitControls(camera, canvas)
controls.target.set(cx, 0, cz)
controls.enableDamping = true
controls.maxPolarAngle = Math.PI / 2 - 0.04
controls.minDistance = 4
controls.maxDistance = 80
controls.update()

window.addEventListener('resize', () => {
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight
    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
})

// --- World map background plane ---
function createWorldMapTexture() {
    const W = 2048, H = 1024
    const c = document.createElement('canvas')
    c.width = W; c.height = H
    const ctx = c.getContext('2d')

    ctx.fillStyle = '#050c23'
    ctx.fillRect(0, 0, W, H)

    function project(lng, lat) {
        return [(lng + 180) / 360 * W, (90 - lat) / 180 * H]
    }

    function drawRings(rings) {
        ctx.beginPath()
        for (const ring of rings) {
            const [x0, y0] = project(ring[0][0], ring[0][1])
            ctx.moveTo(x0, y0)
            for (let i = 1; i < ring.length; i++) {
                const [x, y] = project(ring[i][0], ring[i][1])
                ctx.lineTo(x, y)
            }
            ctx.closePath()
        }
        ctx.fill()
        ctx.stroke()
    }

    ctx.fillStyle = '#1a3660'
    ctx.strokeStyle = '#2d5a9e'
    ctx.lineWidth = 0.6
    ctx.lineJoin = 'round'

    const countries = topojson.feature(worldData, worldData.objects.countries)
    for (const feature of countries.features) {
        const { type, coordinates } = feature.geometry
        if (type === 'Polygon') {
            drawRings(coordinates)
        } else if (type === 'MultiPolygon') {
            for (const polygon of coordinates) drawRings(polygon)
        }
    }

    // Graticule lines
    ctx.strokeStyle = 'rgba(25,65,150,0.4)'
    ctx.lineWidth = 1
    for (const lng of [-150,-120,-90,-60,-30,30,60,90,120,150]) {
        const x = (lng + 180) / 360 * W
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke()
    }
    for (const lat of [60,30,-30,-60]) {
        const y = (90 - lat) / 180 * H
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke()
    }
    // Equator + prime meridian
    ctx.strokeStyle = 'rgba(55,120,245,0.55)'
    ctx.lineWidth = 1.5
    ctx.beginPath(); ctx.moveTo(0, H/2);  ctx.lineTo(W, H/2);  ctx.stroke()
    ctx.beginPath(); ctx.moveTo(W/2, 0);  ctx.lineTo(W/2, H);  ctx.stroke()

    return new THREE.CanvasTexture(c)
}

const mapMesh = new THREE.Mesh(
    new THREE.PlaneGeometry(GRID_COLS, GRID_ROWS),
    new THREE.MeshBasicMaterial({
        map: createWorldMapTexture(),
        transparent: true,
        opacity: 0.65,
        depthWrite: false
    })
)
mapMesh.rotation.x = -Math.PI / 2
mapMesh.position.set(cx, -0.01, cz)
mapMesh.renderOrder = -1
scene.add(mapMesh)

// --- Tile textures ---
function createEmptyTexture() {
    const c = document.createElement('canvas')
    c.width = 128; c.height = 128
    const ctx = c.getContext('2d')
    ctx.clearRect(0, 0, 128, 128)
    ctx.strokeStyle = '#2a6aaa'
    ctx.lineWidth = 3
    ctx.strokeRect(2, 2, 124, 124)
    return new THREE.CanvasTexture(c)
}

function createEmojiTexture(emoji) {
    const c = document.createElement('canvas')
    c.width = 128; c.height = 128
    const ctx = c.getContext('2d')
    ctx.fillStyle = 'rgba(16, 28, 58, 0.75)'
    ctx.fillRect(0, 0, 128, 128)
    ctx.font = emojiFont(80, emoji)
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(emoji, 64, 64)
    return new THREE.CanvasTexture(c)
}

// --- Grid ---
const tileGeometry = new THREE.PlaneGeometry(1 - GAP, 1 - GAP)
const tileMeshes = []
const tileMap = {}

for (let col = 0; col < GRID_COLS; col++) {
    for (let row = 0; row < GRID_ROWS; row++) {
        const mesh = new THREE.Mesh(
            tileGeometry,
            new THREE.MeshBasicMaterial({ map: createEmptyTexture(), side: THREE.DoubleSide, transparent: true })
        )
        mesh.rotation.x = -Math.PI / 2
        mesh.position.set(col, 0, row)
        mesh.userData = { col, row }
        scene.add(mesh)
        tileMeshes.push(mesh)
        tileMap[`${col},${row}`] = mesh
    }
}

function setTileEmoji(col, row, emoji) {
    const key = `${col},${row}`
    tileState[key] = emoji || null
    const mesh = tileMap[key]
    if (!mesh) return
    mesh.material.map.dispose()
    mesh.material.map = emoji ? createEmojiTexture(emoji) : createEmptyTexture()
    mesh.material.needsUpdate = true
}

// Canvas text can't wait on a webfont the way the DOM does: kick off the flag
// slice up front and redraw any flag tiles that were painted before it landed.
document.fonts.load(`80px ${FLAG_FONT}`, '🇮🇹').then(() => {
    for (const [key, emoji] of Object.entries(tileState)) {
        if (emoji && isFlag(emoji)) {
            const [col, row] = key.split(',').map(Number)
            setTileEmoji(col, row, emoji)
        }
    }
})

// --- Geo helpers ---
function latLngToWorldPos(lat, lng) {
    return new THREE.Vector3(
        THREE.MathUtils.clamp(((lng + 180) / 360) * GRID_COLS - 0.5, -0.5, GRID_COLS - 0.5),
        0,
        THREE.MathUtils.clamp(((90 - lat) / 180) * GRID_ROWS - 0.5, -0.5, GRID_ROWS - 0.5)
    )
}

// --- Floating emoji animation ---
class FloatingEmoji {
    constructor(emoji, worldPos) {
        this.startTime = performance.now()
        this.duration = 2400

        // Emoji sprite
        const c = document.createElement('canvas')
        c.width = 128; c.height = 128
        const ctx = c.getContext('2d')
        ctx.beginPath()
        ctx.arc(64, 64, 58, 0, Math.PI * 2)
        ctx.fillStyle = 'rgba(22, 33, 62, 0.85)'
        ctx.fill()
        ctx.strokeStyle = 'rgba(68, 136, 255, 0.7)'
        ctx.lineWidth = 3
        ctx.stroke()
        ctx.font = emojiFont(60, emoji)
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText(emoji, 64, 66)

        const tex = new THREE.CanvasTexture(c)
        this.sprite = new THREE.Sprite(
            new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false })
        )
        this.sprite.scale.setScalar(0.75)
        this.sprite.position.set(worldPos.x, 0.5, worldPos.z)
        scene.add(this.sprite)

        // Ripple ring
        this.ring = new THREE.Mesh(
            new THREE.RingGeometry(0.05, 0.18, 32),
            new THREE.MeshBasicMaterial({
                color: 0x4488ff,
                transparent: true,
                side: THREE.DoubleSide,
                depthWrite: false
            })
        )
        this.ring.rotation.x = -Math.PI / 2
        this.ring.position.set(worldPos.x, 0.02, worldPos.z)
        scene.add(this.ring)
    }

    update() {
        const t = (performance.now() - this.startTime) / this.duration
        if (t >= 1) {
            this._dispose()
            return true
        }

        // Ring expands and fades fast
        const rs = 1 + t * 9
        this.ring.scale.set(rs, rs, rs)
        this.ring.material.opacity = Math.max(0, 0.75 - t * 1.8)

        // Sprite rises with ease-in-out, fades in then out
        const ease = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t
        this.sprite.position.y = 0.5 + ease * 3
        const fadeIn = Math.min(1, t * 6)
        const fadeOut = t > 0.25 ? Math.max(0, 1 - (t - 0.25) / 0.75) : 1
        this.sprite.material.opacity = fadeIn * fadeOut
        this.sprite.scale.setScalar(0.6 + ease * 0.35)

        return false
    }

    _dispose() {
        scene.remove(this.sprite)
        this.sprite.material.map.dispose()
        this.sprite.material.dispose()
        scene.remove(this.ring)
        this.ring.geometry.dispose()
        this.ring.material.dispose()
    }
}

// --- Peer location pulses ---
class PeerPulse {
    constructor(worldPos, color) {
        this.startTime = performance.now()
        this.duration = 2200

        this.ring = new THREE.Mesh(
            new THREE.RingGeometry(0.05, 0.22, 40),
            new THREE.MeshBasicMaterial({ color, transparent: true, side: THREE.DoubleSide, depthWrite: false })
        )
        this.ring.rotation.x = -Math.PI / 2
        this.ring.position.set(worldPos.x, 0.015, worldPos.z)
        scene.add(this.ring)
    }

    update() {
        const t = (performance.now() - this.startTime) / this.duration
        if (t >= 1) { this._dispose(); return true }
        const rs = 1 + t * 5
        this.ring.scale.set(rs, rs, rs)
        this.ring.material.opacity = Math.max(0, 0.7 * (1 - t))
        return false
    }

    _dispose() {
        scene.remove(this.ring)
        this.ring.geometry.dispose()
        this.ring.material.dispose()
    }
}

const activePulses = []

function spawnLocationPulses() {
    if (myLocation) {
        activePulses.push(new PeerPulse(latLngToWorldPos(myLocation.lat, myLocation.lng), 0x44ff99))
    }
    for (const loc of network.getPeerLocations()) {
        activePulses.push(new PeerPulse(latLngToWorldPos(loc.lat, loc.lng), 0x4488ff))
    }
}

const activeAnimations = []

function spawnFloatingEmoji(emoji, location) {
    if (!location) return
    activeAnimations.push(new FloatingEmoji(emoji, latLngToWorldPos(location.lat, location.lng)))
}

// --- Emoji picker (category view -> emoji grid) ---
const picker       = document.getElementById('emoji-picker')
const categoryList = document.getElementById('category-list')
const emojiGrid    = document.getElementById('emoji-grid')
const pickerTitle  = document.getElementById('picker-title')
const pickerBack   = document.getElementById('picker-back')
let selectedTile = null

const EMOJIS_BY_GROUP = new Map(GROUP_ORDER.map(g => [g, []]))
for (const emoji of SHOWN) {
    const list = EMOJIS_BY_GROUP.get(emojiData[emoji]?.group)
    if (list) list.push(emoji)
}

// Category rows are built once; emoji grids are rebuilt when a category opens.
for (const group of GROUP_ORDER) {
    const emojis = EMOJIS_BY_GROUP.get(group)
    if (!emojis.length) continue

    const row = document.createElement('button')
    row.className = 'category-btn'
    row.style.setProperty('--group-color', GROUP_COLOR[group])
    row.dataset.group = group

    const icon = document.createElement('span')
    icon.className = 'category-icon'
    icon.textContent = emojis[0]
    applyFlagFont(icon, emojis[0])

    const name = document.createElement('span')
    name.className = 'category-name'
    name.textContent = group

    const count = document.createElement('span')
    count.className = 'category-count'
    count.textContent = emojis.length

    row.append(icon, name, count)
    categoryList.appendChild(row)
}

function showCategories() {
    pickerTitle.textContent = 'Pick a category'
    pickerTitle.style.color = ''
    pickerBack.style.visibility = 'hidden'
    emojiGrid.style.display = 'none'
    emojiGrid.replaceChildren()
    hideTooltip()
    categoryList.style.display = 'flex'
}

function showGroup(group) {
    pickerTitle.textContent = group
    pickerTitle.style.color = GROUP_COLOR[group]
    pickerBack.style.visibility = 'visible'
    categoryList.style.display = 'none'

    const frag = document.createDocumentFragment()
    for (const emoji of EMOJIS_BY_GROUP.get(group)) {
        const btn = document.createElement('button')
        btn.className = 'emoji-btn'
        btn.textContent = emoji
        btn.dataset.emoji = emoji
        applyFlagFont(btn, emoji)
        frag.appendChild(btn)
    }
    emojiGrid.replaceChildren(frag)
    emojiGrid.scrollTop = 0
    emojiGrid.style.display = 'grid'
}

categoryList.addEventListener('click', (e) => {
    const row = e.target.closest('.category-btn')
    if (row) showGroup(row.dataset.group)
})

emojiGrid.addEventListener('click', (e) => {
    const btn = e.target.closest('.emoji-btn')
    if (!btn || !selectedTile) return
    const emoji = btn.dataset.emoji
    const { col, row } = selectedTile
    setTileEmoji(col, row, emoji)
    network.broadcastTileUpdate(col, row, emoji)
    spawnFloatingEmoji(emoji, myLocation)
    closePicker()
})

pickerBack.addEventListener('click', showCategories)
document.getElementById('close-picker').addEventListener('click', closePicker)

document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || picker.style.display === 'none') return
    // Escape backs out of a category first, then closes the picker.
    if (emojiGrid.style.display === 'grid') showCategories()
    else closePicker()
})

function closePicker() {
    picker.style.display = 'none'
    selectedTile = null
    hideTooltip()
}

function showPicker(x, y) {
    showCategories()
    picker.style.display = 'block'
    const pw = picker.offsetWidth
    const ph = picker.offsetHeight
    const left = Math.max(8, Math.min(x - pw / 2, sizes.width - pw - 8))
    const top = Math.max(8, Math.min(y - ph - 14, sizes.height - ph - 8))
    picker.style.left = left + 'px'
    picker.style.top = top + 'px'
}

// --- Click / raycaster ---
const raycaster = new THREE.Raycaster()
const mouse = new THREE.Vector2()
let pointerDownPos = { x: 0, y: 0 }

canvas.addEventListener('pointerdown', (e) => { pointerDownPos = { x: e.clientX, y: e.clientY } })

canvas.addEventListener('click', (e) => {
    const dx = e.clientX - pointerDownPos.x
    const dy = e.clientY - pointerDownPos.y
    if (dx * dx + dy * dy > 25) return

    mouse.x = (e.clientX / sizes.width) * 2 - 1
    mouse.y = -(e.clientY / sizes.height) * 2 + 1
    raycaster.setFromCamera(mouse, camera)

    const hits = raycaster.intersectObjects(tileMeshes)
    if (hits.length > 0) {
        selectedTile = hits[0].object.userData
        showPicker(e.clientX, e.clientY)
    } else {
        closePicker()
    }
})

// --- Status ---
const statusEl = document.getElementById('status')

// --- Network ---
const network = new Network({
    onReady: ({ location }) => {
        myLocation = location
        spawnLocationPulses()
        setInterval(spawnLocationPulses, 4000)
    },
    onTileUpdate: (col, row, emoji, senderLocation) => {
        setTileEmoji(col, row, emoji)
        spawnFloatingEmoji(emoji, senderLocation)
    },
    onStatusChange: (msg) => { statusEl.textContent = msg }
})
network.connect()

// --- Render loop ---
const tick = (ts) => {
    advanceCarousel(ts)
    controls.update()

    for (let i = activeAnimations.length - 1; i >= 0; i--) {
        if (activeAnimations[i].update()) activeAnimations.splice(i, 1)
    }

    for (let i = activePulses.length - 1; i >= 0; i--) {
        if (activePulses[i].update()) activePulses.splice(i, 1)
    }

    renderer.render(scene, camera)
    window.requestAnimationFrame(tick)
}
requestAnimationFrame(tick)
